/**
 * Note registry (architecture §4 — the `notes/{noteId}` collection). A small catalog record per
 * note so any device can enumerate a user's pages without holding every Y.Doc locally. The body of
 * a note still lives as a Yjs log + markdown snapshot in Cloud Storage (the source of truth, §4);
 * this is metadata + GCS pointers only. Partitions by `userId` like everything else.
 *
 * Written server-side from the sync server's store hook (so it can't be skipped by an offline
 * client) and read back over `GET /notes`.
 */
import { PageType } from '../types.js';
import { isDailyJournalId } from '../journal/daily.js';

export interface NoteRecord {
  noteId: string;
  userId: string;
  /** journal (date-keyed, list-first) or document (prose-first) — derived from the noteId. */
  type: PageType;
  /** first non-empty line of the note, for the list view; "Untitled" when empty. */
  title: string;
  /** page icon (emoji or "material:name") for the list view; absent/null when unset. */
  icon?: string | null;
  /**
   * Page-level tags (from `DocAttributes.tags`), for search filtering + the switcher (FIND-01).
   * Written server-side from the store hook; absent on legacy records until backfilled.
   */
  tags?: string[];
  /**
   * Alternate names for this page (from `DocAttributes.aliases`), so search/the switcher can match
   * and badge alias rows (FIND-01). Written server-side from the store hook; absent until backfilled.
   */
  aliases?: string[];
  /**
   * FACET — the page's entity type + properties, PROJECTED from `DocAttributes` by the store hook
   * (like tags/aliases) so `lookup_entity`, the brief, and the search producer read them without
   * opening the Y.Doc. The doc stays the source of truth.
   */
  entityType?: import('../types.js').NoteEntityType | null;
  properties?: Record<string, import('../types.js').PropertyValue> | null;
  /** last debounced server-side save, epoch ms. */
  updatedAt: number;
  /**
   * When (epoch ms) the note was last OPENED, by any of the user's devices — the sole basis for the
   * cross-device Recent list. Stamped by `POST /notes/:id/open`; cleared (null) when removed from
   * Recent. Absent/null means it isn't in Recent. Never written by the sync store hook, so a save
   * never clobbers it (Firestore upsert is merge).
   */
  openedAt?: number | null;
  /**
   * Google Calendar eventId this note is bound to (a meeting note), if any. Set by the app-api's
   * bind-event endpoint (merge-write, like `openedAt`, so the sync store hook never clobbers it). Lets
   * the Initiatives calendar bridge propagate the note's `+initiative` mentions onto the event's edge.
   */
  eventId?: string | null;
  /**
   * Whether the note is a favorite (starred), per user and shared across all their devices. Toggled by
   * `POST /notes/:id/star`; absent/false means not favorited. Like `openedAt`, this is written only by
   * the app backend (never by the sync store hook, whose Firestore upsert is a merge), so a save never
   * clobbers it.
   */
  starred?: boolean | null;
  /** GCS object path of the authoritative binary Yjs log (architecture §4 pointer). */
  updatePath: string;
  /** GCS object path of the markdown snapshot mirror. */
  snapshotPath: string;
  /** true when the snapshot dropped non-round-tripping nodes (the claw-mirror caveat, §3). */
  lossy: boolean;
}

/**
 * Best-effort type guess from the noteId's naming convention (journals are date-keyed
 * `journal-YYYY-MM-DD`; everything else is a document). This is a CLIENT-SIDE FALLBACK only — for
 * an optimistic label before the registry loads. The authoritative type is the doc's `pageType`
 * attribute, which the registry record carries (written server-side from the store hook).
 */
export function noteTypeFromId(noteId: string): PageType {
  return isDailyJournalId(noteId) ? PageType.Journal : PageType.Document;
}

/** Canonical GCS object paths for a note (must match server-sync's DocStorage layout, §4/§10). */
export function noteStoragePaths(userId: string, noteId: string): {
  updatePath: string;
  snapshotPath: string;
} {
  return {
    updatePath: `${userId}/${noteId}/updates.bin`,
    snapshotPath: `${userId}/${noteId}/snapshot.md`,
  };
}
